// pages/FormasPago.js
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Card,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  AppstoreOutlined,
  ExportOutlined,
  BarcodeOutlined,
  FileTextOutlined,
  DollarOutlined,
  CreditCardOutlined,
  BankOutlined,
  WalletOutlined,
  MoneyCollectOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const FormasPago = () => {
  // Estados
  const [formasPago, setFormasPago] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentFormaPago, setCurrentFormaPago] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  // Estados para filtros y ordenación
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  // Configuración de columnas de la tabla
  const columns = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      width: 100,
      sorter: (a, b) => a.codigo?.localeCompare(b.codigo || '') || 0,
      sortOrder: sortedInfo.columnKey === 'codigo' && sortedInfo.order,
      render: (codigo) => (
        <Tag 
          color="blue" 
          style={{ 
            fontWeight: 'bold', 
            fontSize: '14px',
            padding: '4px 8px'
          }}
        >
          {codigo}
        </Tag>
      ),
      fixed: 'left',
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 200,
      sorter: (a, b) => a.descripcion?.localeCompare(b.descripcion || '') || 0,
      sortOrder: sortedInfo.columnKey === 'descripcion' && sortedInfo.order,
      render: (descripcion) => (
        <div style={{ maxWidth: 200 }}>
          <Text strong style={{ fontSize: '13px' }}>
            {descripcion}
          </Text>
        </div>
      ),
    },
    {
      title: 'Mostrar',
      dataIndex: 'mostrar',
      key: 'mostrar',
      width: 100,
      render: (mostrar) => (
        mostrar ? (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
        ) : (
          <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />
        )
      ),
      filters: [
        { text: 'Sí', value: true },
        { text: 'No', value: false },
      ],
      filteredValue: filteredInfo.mostrar || null,
      onFilter: (value, record) => record.mostrar === value,
    },
    {
      title: 'Cuenta Contable',
      dataIndex: 'cuentacontable',
      key: 'cuentacontable',
      width: 150,
      render: (cuentacontable) => (
        cuentacontable ? (
          <Tag color="green" style={{ fontFamily: 'monospace' }}>
            {cuentacontable}
          </Tag>
        ) : (
          <Text type="secondary" style={{ fontSize: '12px' }}>
            No definida
          </Text>
        )
      ),
    },
    {
      title: 'Cuenta Depósito',
      dataIndex: 'CuentaDeposito',
      key: 'CuentaDeposito',
      width: 150,
      render: (CuentaDeposito) => (
        CuentaDeposito ? (
          <Tag color="orange" style={{ fontFamily: 'monospace' }}>
            {CuentaDeposito}
          </Tag>
        ) : (
          <Text type="secondary" style={{ fontSize: '12px' }}>
            No definida
          </Text>
        )
      ),
    },
    {
      title: 'Traspasar',
      dataIndex: 'traspasar',
      key: 'traspasar',
      width: 100,
      render: (traspasar) => (
        traspasar ? (
          <Badge
            status="processing"
            text="Sí"
            style={{ color: '#1890ff', fontWeight: 'bold' }}
          />
        ) : (
          <Badge
            status="default"
            text="No"
            style={{ color: '#8c8c8c', fontWeight: 'bold' }}
          />
        )
      ),
      filters: [
        { text: 'Sí', value: true },
        { text: 'No', value: false },
      ],
      filteredValue: filteredInfo.traspasar || null,
      onFilter: (value, record) => record.traspasar === value,
    },
    {
      title: 'Es Efectivo',
      dataIndex: 'esEfectivo',
      key: 'esEfectivo',
      width: 120,
      render: (esEfectivo) => (
        esEfectivo ? (
          <Badge
            status="success"
            text="Sí"
            style={{ color: '#52c41a', fontWeight: 'bold' }}
          />
        ) : (
          <Badge
            status="default"
            text="No"
            style={{ color: '#8c8c8c', fontWeight: 'bold' }}
          />
        )
      ),
      filters: [
        { text: 'Sí', value: true },
        { text: 'No', value: false },
      ],
      filteredValue: filteredInfo.esEfectivo || null,
      onFilter: (value, record) => record.esEfectivo === value,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
          
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar forma de pago?"
            description="Esta acción eliminará la forma de pago permanentemente"
            onConfirm={() => handleDelete(record.codigo)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Cargar datos iniciales
  useEffect(() => {
    fetchFormasPago();
  }, []);

  const fetchFormasPago = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Parámetros de la query
      const queryParams = {
        search: searchText,
        sortField: params.sortField || sortedInfo.field || 'codigo',
        sortOrder: params.sortOrder || (sortedInfo.order === 'descend' ? 'DESC' : 'ASC')
      };
      
      // Añadir filtros
      if (filteredInfo.mostrar) {
        queryParams.mostrar = filteredInfo.mostrar[0];
      }
      if (filteredInfo.traspasar) {
        queryParams.traspasar = filteredInfo.traspasar[0];
      }
      if (filteredInfo.esEfectivo) {
        queryParams.esEfectivo = filteredInfo.esEfectivo[0];
      }
      
      const response = await axios.get(`${BASE_URL}/api/formaspago`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });

      const cleanedData = (response.data.data || []).map(item => ({
        ...item,
        descripcion: item.descripcion || '',
        cuentacontable: item.cuentacontable || '',
        CuentaDeposito: item.CuentaDeposito || '',
        mostrar: Boolean(item.mostrar),
        traspasar: Boolean(item.traspasar),
        esEfectivo: Boolean(item.esEfectivo)
      }));
      
      setFormasPago(cleanedData);
      
    } catch (error) {
      console.error('❌ Error cargando formas de pago:', error);
      message.error('Error al cargar las formas de pago');
      setFormasPago([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  // Handlers para las acciones
  const handleCreate = async () => {
    setModalType('create');
    setCurrentFormaPago(null);
    form.resetFields();
    
    // Establecer valores por defecto
    form.setFieldsValue({
      mostrar: true,
      traspasar: false,
      esEfectivo: true,
      cuentacontable: '',
      CuentaDeposito: ''
    });
    
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentFormaPago(record);
    
    const formValues = {
      codigo: record.codigo,
      descripcion: record.descripcion || '',
      mostrar: record.mostrar || true,
      cuentacontable: record.cuentacontable || '',
      CuentaDeposito: record.CuentaDeposito || '',
      traspasar: record.traspasar || false,
      esEfectivo: record.esEfectivo || true
    };
    
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleView = (record) => {
    const icon = record.esEfectivo ? <MoneyCollectOutlined /> : <CreditCardOutlined />;
    
    Modal.info({
      title: `Forma de Pago ${record.codigo}`,
      width: 600,
      icon: icon,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <strong>Código:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="blue" style={{ fontSize: '18px', padding: '6px 12px' }}>
                  {record.codigo}
                </Tag>
              </div>
            </Col>
            
            <Col span={24}>
              <strong>Descripción:</strong>
              <div style={{ 
                marginTop: 4, 
                padding: '12px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '6px',
                fontSize: '14px'
              }}>
                {record.descripcion || 'Sin descripción'}
              </div>
            </Col>
            
            <Col span={12}>
              <strong>Cuenta Contable:</strong>
              <div style={{ marginTop: 4 }}>
                {record.cuentacontable ? (
                  <Tag color="green" style={{ fontSize: '14px', padding: '4px 8px' }}>
                    {record.cuentacontable}
                  </Tag>
                ) : (
                  <Text type="secondary" italic>No definida</Text>
                )}
              </div>
            </Col>
            
            <Col span={12}>
              <strong>Cuenta Depósito:</strong>
              <div style={{ marginTop: 4 }}>
                {record.CuentaDeposito ? (
                  <Tag color="orange" style={{ fontSize: '14px', padding: '4px 8px' }}>
                    {record.CuentaDeposito}
                  </Tag>
                ) : (
                  <Text type="secondary" italic>No definida</Text>
                )}
              </div>
            </Col>
            
            <Col span={8}>
              <strong>Mostrar:</strong>
              <div style={{ marginTop: 4 }}>
                {record.mostrar ? (
                  <Badge 
                    status="success" 
                    text="Sí" 
                    style={{ fontSize: '14px', fontWeight: 'bold' }}
                  />
                ) : (
                  <Badge 
                    status="default" 
                    text="No" 
                    style={{ fontSize: '14px', fontWeight: 'bold' }}
                  />
                )}
              </div>
            </Col>
            
            <Col span={8}>
              <strong>Traspasar:</strong>
              <div style={{ marginTop: 4 }}>
                {record.traspasar ? (
                  <Badge 
                    status="processing" 
                    text="Sí" 
                    style={{ fontSize: '14px', fontWeight: 'bold' }}
                  />
                ) : (
                  <Badge 
                    status="default" 
                    text="No" 
                    style={{ fontSize: '14px', fontWeight: 'bold' }}
                  />
                )}
              </div>
            </Col>
            
            <Col span={8}>
              <strong>Es Efectivo:</strong>
              <div style={{ marginTop: 4 }}>
                {record.esEfectivo ? (
                  <Badge 
                    status="success" 
                    text="Sí" 
                    style={{ fontSize: '14px', fontWeight: 'bold' }}
                  />
                ) : (
                  <Badge 
                    status="default" 
                    text="No" 
                    style={{ fontSize: '14px', fontWeight: 'bold' }}
                  />
                )}
              </div>
            </Col>
            
            <Col span={24}>
              <Divider />
              <Card size="small" style={{ backgroundColor: '#f6ffed' }}>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  <strong>💡 Información:</strong>
                  <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                    <li><strong>Mostrar:</strong> Determina si esta forma de pago aparece en los listados del sistema</li>
                    <li><strong>Traspasar:</strong> Indica si los pagos con esta forma deben traspasarse automáticamente</li>
                    <li><strong>Es Efectivo:</strong> Define si es una forma de pago en efectivo (caja) o no (tarjeta, transferencia)</li>
                    <li><strong>Cuenta Contable:</strong> Cuenta para la integración con el sistema contable</li>
                    <li><strong>Cuenta Depósito:</strong> Cuenta bancaria específica para depósitos</li>
                  </ul>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleDelete = async (codigo) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BASE_URL}/api/formaspago/${codigo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Filtrar el eliminado
      setFormasPago(formasPago.filter(fp => fp.codigo !== codigo));
      
      message.success('Forma de pago eliminada correctamente');
    } catch (error) {
      console.error('❌ Error eliminando forma de pago:', error);
      message.error(error.response?.data?.error || 'Error al eliminar la forma de pago');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');

      if (modalType === 'create') {
        const response = await axios.post(
          `${BASE_URL}/api/formaspago`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchFormasPago();
        message.success('Forma de pago creada correctamente');
      } else {
        const response = await axios.put(
          `${BASE_URL}/api/formaspago/${currentFormaPago.codigo}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Actualizar el estado local
        const formaPagoActualizada = {
          ...response.data.data,
          ...values
        };
        
        setFormasPago(prevFormasPago => 
          prevFormasPago.map(fp => 
            fp.codigo === currentFormaPago.codigo
              ? formaPagoActualizada 
              : fp
          )
        );
        
        message.success('Forma de pago actualizada correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      
    } catch (error) {
      console.error('❌ Error guardando forma de pago:', error);
      message.error(error.response?.data?.error || 'Error al guardar los datos');
    }
  };

  const handleSearch = () => {
    if (searchText.trim() === '') {
      message.warning('Por favor ingrese un término de búsqueda');
      return;
    }
    setSearchLoading(true);
    fetchFormasPago({});
  };

  const handleResetSearch = () => {
    setSearchText('');
    setFilteredInfo({});
    setSortedInfo({});
    setSearchLoading(false);
    fetchFormasPago({});
  };

  const handleExport = () => {
    message.info('Función de exportación en desarrollo');
  };

  const handleSort = (field) => {
    const newSortedInfo = {
      field,
      order: sortedInfo.field === field && sortedInfo.order === 'ascend' ? 'descend' : 'ascend'
    };
    setSortedInfo(newSortedInfo);
    
    const params = {
      sortField: field,
      sortOrder: newSortedInfo.order === 'ascend' ? 'ASC' : 'DESC'
    };
    
    fetchFormasPago(params);
  };

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Input
          placeholder="Buscar por código, descripción, cuenta contable..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 350 }}
          onPressEnter={handleSearch}
          allowClear
          onClear={handleResetSearch}
          prefix={<SearchOutlined />}
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={searchLoading}
        >
          Buscar
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleResetSearch}
        >
          Restablecer
        </Button>
        <span style={{ color: '#666', fontSize: '12px' }}>
          {formasPago.length.toLocaleString()} formas de pago
        </span>
      </Space>
    </div>
  );

  // Estadísticas
  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Statistic 
          title="Total Formas de Pago" 
          value={formasPago.length}
          prefix={<AppstoreOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Mostrar" 
          value={formasPago.filter(fp => fp.mostrar).length}
          suffix={`/ ${formasPago.length}`}
          prefix={<EyeOutlined />}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Es Efectivo" 
          value={formasPago.filter(fp => fp.esEfectivo).length}
          suffix={`/ ${formasPago.length}`}
          prefix={<MoneyCollectOutlined />}
          valueStyle={{ color: '#fa8c16' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Traspasar" 
          value={formasPago.filter(fp => fp.traspasar).length}
          suffix={`/ ${formasPago.length}`}
          prefix={<BankOutlined />}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col>
    </Row>
  );

  return (
    <>
      <PageContainer
        title="Formas de Pago"
        subtitle={`${formasPago.length} formas de pago registradas`}
        icon={<CreditCardOutlined />}
        loading={loading}
        onRefresh={() => fetchFormasPago()}
        onCreate={handleCreate}
        badgeCount={formasPago.length}
        badgeStatus="processing"
        extra={
          <Space>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
            >
              Exportar
            </Button>
            <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
              <strong>{formasPago.length.toLocaleString()}</strong> formas de pago
            </Tag>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Estadísticas */}
        {estadisticas}
        
        {/* Barra de búsqueda */}
        {searchBar}
        
        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={formasPago}
          rowKey={(record) => record.codigo}
          loading={loading}
          onChange={(pagination, filters, sorter) => {
            setFilteredInfo(filters);
            setSortedInfo(sorter);
            fetchFormasPago({
              sortField: sorter.field,
              sortOrder: sorter.order === 'ascend' ? 'ASC' : 'DESC'
            });
          }}
          scroll={{ x: 1400, y: 600 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
          size="middle"
          locale={{
            emptyText: 'No se encontraron formas de pago'
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar forma de pago */}
      <Modal
        title={modalType === 'create' ? 'Nueva Forma de Pago' : 'Editar Forma de Pago'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            mostrar: true,
            traspasar: false,
            esEfectivo: true,
            cuentacontable: '',
            CuentaDeposito: ''
          }}
        >
          {/* Primera fila: Código y Descripción */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="codigo"
                label="Código"
                rules={[
                  { 
                    required: true, 
                    message: 'Por favor ingrese el código',
                    max: 10,
                    whitespace: false
                  }
                ]}
                tooltip="Código único de la forma de pago (máx. 10 caracteres)"
              >
                <Input 
                  placeholder="Ej: EFE, TAR, TRN"
                  maxLength={10}
                  disabled={modalType === 'edit'}
                  prefix={<BarcodeOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="descripcion"
                label="Descripción"
                rules={[
                  { 
                    required: true, 
                    message: 'Por favor ingrese la descripción',
                    max: 100
                  }
                ]}
                tooltip="Descripción completa de la forma de pago"
              >
                <Input 
                  placeholder="Descripción de la forma de pago"
                  maxLength={100}
                  showCount
                  prefix={<FileTextOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Segunda fila: Cuentas */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="cuentacontable"
                label="Cuenta Contable"
                tooltip="Código de cuenta contable (opcional)"
              >
                <Input 
                  placeholder="Ej: 5700000002"
                  maxLength={50}
                  prefix={<DollarOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="CuentaDeposito"
                label="Cuenta Depósito"
                tooltip="Cuenta bancaria para depósitos (opcional)"
              >
                <Input 
                  placeholder="Ej: 0123456789"
                  maxLength={50}
                  prefix={<BankOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Tercera fila: Switches */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="mostrar"
                label="Mostrar"
                valuePropName="checked"
                tooltip="Determina si esta forma de pago aparece en los listados"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                  style={{ minWidth: 80 }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="traspasar"
                label="Traspasar"
                valuePropName="checked"
                tooltip="Indica si los pagos se traspasan automáticamente"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                  style={{ minWidth: 80 }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="esEfectivo"
                label="Es Efectivo"
                valuePropName="checked"
                tooltip="Define si es una forma de pago en efectivo"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                  style={{ minWidth: 80 }}
                />
              </Form.Item>
            </Col>
          </Row>

          {modalType === 'create' && (
            <Card size="small" style={{ marginTop: 16, backgroundColor: '#f6ffed' }}>
              <div style={{ fontSize: '13px', color: '#666' }}>
                <strong>📝 Información importante:</strong>
                <ul style={{ marginTop: 8, marginBottom: 4, paddingLeft: 20 }}>
                  <li><strong>Código:</strong> Identificador único (ej: EFE, TAR, CHQ, TRN)</li>
                  <li><strong>Descripción:</strong> Nombre completo de la forma de pago</li>
                  <li><strong>Cuenta Contable:</strong> Opcional - para integración contable</li>
                  <li><strong>Cuenta Depósito:</strong> Opcional - cuenta bancaria específica</li>
                  <li><strong>Mostrar:</strong> Activar para que aparezca en listados</li>
                  <li><strong>Traspasar:</strong> Activar para traspaso automático de pagos</li>
                  <li><strong>Es Efectivo:</strong> Activar para formas de pago en efectivo</li>
                </ul>
                <div style={{ marginTop: 8 }}>
                  <strong>💡 Ejemplos comunes:</strong>
                  <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 20 }}>
                    <li><strong>EFE</strong> - Efectivo (esEfectivo: Sí, traspasar: Sí)</li>
                    <li><strong>TAR</strong> - Tarjeta de crédito/débito (esEfectivo: No)</li>
                    <li><strong>CHQ</strong> - Cheque (esEfectivo: No)</li>
                    <li><strong>TRN</strong> - Transferencia bancaria (esEfectivo: No)</li>
                    <li><strong>ABO</strong> - Abono/nota de crédito (mostrar: Sí)</li>
                    <li><strong>AME</strong> - American Express (esEfectivo: No)</li>
                  </ul>
                </div>
              </div>
            </Card>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default FormasPago;